/**
 * @lume/shared — Motor de match puro de waitlist (Frente B Bloco IV PR2).
 *
 * `matchWaitlistEntries` é juiz-independente: recebe dados carregados, não faz I/O.
 * Sem Prisma, sem NestJS. Fuso-safe via `localDayOfWeek` e `zonedWallTimeToUtc` (reuso
 * do mesmo padrão de `compute-availability.ts`).
 *
 * Pipeline:
 *  1. Filtra elegíveis por serviço/recurso/preferência/optIn (seta isAlternative).
 *  2. Filtra por janela (weekday + startMinute..endMinute + fromDate/untilDate no fuso).
 *  3. Filtra por lead-time (gap.startsAt - now >= leadTimeMin).
 *  4. Calcula tier efetivo (aplica no-show demote quando enabled e count >= threshold).
 *  5. Ordena por (TIER_RANK[tierEfetivo] ASC, createdAt ASC) — FIFO dentro do tier.
 *  6. Retorna `{ entryId, isAlternative }[]`.
 */
import { localDayOfWeek } from './internal';
import { zonedWallTimeToUtc } from './tz';
// ─── Ranking de tiers (menor = maior prioridade) ──────────────────────────────
/**
 * Rank canônico dos tiers de prioridade da fila (menor = maior prioridade).
 * Fonte ÚNICA da ordenação: o motor de match (`matchWaitlistEntries`) E a
 * listagem do operador (PR6 — `WaitlistService.listWaitlist`) ordenam pelo
 * MESMO rank, então o painel mostra a fila na ordem EXATA em que a promoção a
 * percorre (sem drift entre a UI e o motor). Exportado p/ reuso no engine.
 */
export const WAITLIST_TIER_RANK = {
    bumped: 0,
    urgent_high: 1,
    urgent_medium: 2,
    urgent_low: 3,
    preferred: 4,
    normal: 5,
};
/** Alias interno (compat com os usos abaixo). */
const TIER_RANK = WAITLIST_TIER_RANK;
// ─── Helpers internos ─────────────────────────────────────────────────────────
/**
 * Verifica se a vaga (gap) cai dentro de uma janela do candidato no fuso dado.
 *
 * Critérios (todos devem ser satisfeitos):
 * 1. gap.startsAt cai no dia da semana local coberto pela janela (window.weekdays).
 * 2. gap.startsAt >= início local da janela (startMinute) no fuso.
 * 3. gap.endsAt <= fim local da janela (endMinute) no fuso.
 * 4. Se fromDate: gap.startsAt >= fromDate.
 * 5. Se untilDate: gap.startsAt <= untilDate.
 */
function gapFitsWindow(gapStartsAt, gapEndsAt, window, timezone) {
    // 4. fromDate check
    if (window.fromDate != null && gapStartsAt < window.fromDate)
        return false;
    // 5. untilDate check
    if (window.untilDate != null && gapStartsAt > window.untilDate)
        return false;
    // 1. dia da semana local
    const dayOfWeek = localDayOfWeek(gapStartsAt, timezone);
    if (!window.weekdays.includes(dayOfWeek))
        return false;
    // 2+3. startMinute..endMinute no fuso do recurso
    // Projeta startMinute e endMinute da janela para UTC neste dia âncora
    const windowStart = zonedWallTimeToUtc(gapStartsAt, window.startMinute, timezone);
    const windowEnd = zonedWallTimeToUtc(gapStartsAt, window.endMinute, timezone);
    if (gapStartsAt < windowStart)
        return false;
    if (gapEndsAt > windowEnd)
        return false;
    return true;
}
/**
 * Retorna o tier efetivo, aplicando a penalidade de no-show se configurada.
 */
function effectiveTier(candidate, noShowPenalty) {
    if (noShowPenalty != null &&
        noShowPenalty.enabled &&
        (candidate.personNoShowCount ?? 0) >= noShowPenalty.threshold) {
        // Clamp: a penalidade só pode REBAIXAR (rank maior). Se o `demote` configurado
        // tiver rank menor (mais prioritário) que o tier atual, ignorar — penalizar
        // nunca pode promover.
        const demotedRank = TIER_RANK[noShowPenalty.demote];
        const originalRank = TIER_RANK[candidate.priorityTier];
        return demotedRank > originalRank ? noShowPenalty.demote : candidate.priorityTier;
    }
    return candidate.priorityTier;
}
// ─── matchWaitlistEntries ─────────────────────────────────────────────────────
/**
 * Dado um gap aberto, retorna os candidatos elegíveis e ordenados da fila de espera.
 *
 * Pura, sem IO, sem `Date.now()` — usa `input.now`.
 */
export function matchWaitlistEntries(input) {
    const { gap, candidates, eligibleResourceIdsForService, leadTimeMin, now, timezone, noShowPenalty } = input;
    // Lead-time: gap.startsAt - now >= leadTimeMin
    const leadTimeMs = leadTimeMin * 60_000;
    if (gap.startsAt.getTime() - now.getTime() < leadTimeMs) {
        return [];
    }
    const eligible = [];
    for (const candidate of candidates) {
        // ── Elegibilidade por recurso/preferência ─────────────────────────────────
        let isAlternative = false;
        if (candidate.resourcePreference === 'specific') {
            const wantedResourceId = candidate.preferredResourceId;
            if (wantedResourceId === gap.resourceId) {
                // Recurso preferido — elegível (não é alternativa)
                isAlternative = false;
            }
            else if (candidate.alternativeOffersOptIn) {
                // Recurso diferente, mas aceita alternativas —
                // o gap.resourceId deve estar na matriz de elegíveis para o serviço
                if (!eligibleResourceIdsForService.includes(gap.resourceId)) {
                    continue;
                }
                isAlternative = true;
            }
            else {
                // Recurso diferente e não aceita alternativas → pula
                continue;
            }
        }
        else {
            // any_eligible: gap.resourceId deve estar na matriz de elegíveis
            if (!eligibleResourceIdsForService.includes(gap.resourceId)) {
                continue;
            }
            isAlternative = false;
        }
        // ── Elegibilidade por janela ───────────────────────────────────────────────
        const hasMatchingWindow = candidate.windows.some((w) => gapFitsWindow(gap.startsAt, gap.endsAt, w, timezone));
        if (!hasMatchingWindow)
            continue;
        // ── Tier efetivo (no-show penalty) ───────────────────────────────────────
        const tier = effectiveTier(candidate, noShowPenalty);
        eligible.push({ candidate, isAlternative, tier });
    }
    // ── Ordenação: (TIER_RANK ASC, createdAt ASC) ─────────────────────────────
    eligible.sort((a, b) => {
        const rankDiff = TIER_RANK[a.tier] - TIER_RANK[b.tier];
        if (rankDiff !== 0)
            return rankDiff;
        return a.candidate.createdAt.getTime() - b.candidate.createdAt.getTime();
    });
    return eligible.map(({ candidate, isAlternative }) => ({
        entryId: candidate.entryId,
        isAlternative,
    }));
}
//# sourceMappingURL=waitlist-match.js.map